import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

interface StructureDatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  testId?: string;
}

export function StructureDatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  testId,
}: StructureDatePickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState(false);

  const handleDateSelect = (date: Date | undefined) => {
    onChange(date);
    setOpen(false);
    setInputValue("");
    setInputError(false);
  };

  const handleKeyboardEntry = (inputDate: string) => {
    setInputError(false);
    const parsedDate = parse(inputDate, "dd/MM/yyyy", new Date());
    if (isValid(parsedDate) && parsedDate.getFullYear() >= 1900 && parsedDate.getFullYear() <= 2100) {
      handleDateSelect(parsedDate);
    } else {
      setInputError(true);
      setTimeout(() => setInputError(false), 3000);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground"
          )}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "d MMM yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="space-y-2">
          <Input
            type="text"
            placeholder="DD/MM/YYYY"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && inputValue) {
                handleKeyboardEntry(inputValue);
              }
            }}
            className={cn(
              "w-full",
              inputError && "border-destructive"
            )}
          />
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleDateSelect}
            initialFocus
            captionLayout="dropdown"
            fromYear={1900}
            toYear={2100}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
